function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
document.addEventListener("DOMContentLoaded", () => {
  // --- Create Lightbox ---
  const lightbox = document.createElement("div");
  lightbox.className = "ugc-lightbox";
  lightbox.innerHTML = `
    <div class="ugc-lightbox__inner">
      <div class="ugc-lightbox__video">
        <video controls autoplay muted playsinline></video>
      </div>
      <div class="ugc-lightbox__product"></div>
      <span class="ugc-lightbox__close">&times;</span>
    </div>`;
  document.body.appendChild(lightbox);

  const videoEl = lightbox.querySelector("video");
  const productEl = lightbox.querySelector(".ugc-lightbox__product");

  // iOS inline playback safety
  videoEl.setAttribute("playsinline", "");
  videoEl.setAttribute("webkit-playsinline", "true");

  // === Helpers ===============================================================
  function getReelMedia(reel){
    const v = reel.querySelector(".ugc-thumb-video");
    const src =
      v?.querySelector("source")?.src ||
      v?.currentSrc || v?.src ||
      v?.dataset?.src || v?.getAttribute?.("data-src") || null;
    const poster = v?.poster || reel.querySelector(".ugc-home__thumb img")?.src || null;
    return { src, poster };
  }
  function cleanupVideo(){
    try {
      videoEl.pause();
      videoEl.removeAttribute("poster");
      videoEl.removeAttribute("src");
      while (videoEl.firstChild) videoEl.removeChild(videoEl.firstChild);
    } catch {}
  }

  // --- Toast helpers (Added to cart) ----------------------------------------
  function ensureUGCToastStyles(){
    if (document.getElementById("ugc-lb-toast")) return;
    const css = `
      .ugc-toast{
        position:fixed;
        left:50%;
        bottom:calc(env(safe-area-inset-bottom) + 16px);
        transform:translateX(-50%) translateY(8px);
        background:#fff; color:#111;
        border:1px solid #e5e7eb; border-radius:12px;
        padding:10px 14px; font-size:14px; line-height:1.3; font-weight:600;
        box-shadow:0 10px 24px rgba(0,0,0,.14);
        z-index:100002; opacity:0; pointer-events:none;
        transition:opacity .18s ease, transform .18s ease;
      }
      .ugc-toast.is-shown{ opacity:1; transform:translateX(-50%) translateY(0); pointer-events:auto; }
      .ugc-toast--error{ border-color:#fca5a5; background:#fff5f5; color:#991b1b; }
    `;
    const st = document.createElement("style");
    st.id = "ugc-lb-toast"; st.textContent = css;
    document.head.appendChild(st);
  }
  function showUGCToast(msg, kind = "ok"){
    ensureUGCToastStyles();
    let t = lightbox.querySelector(".ugc-toast");
    if (!t) {
      t = document.createElement("div");
      t.className = "ugc-toast";
      t.setAttribute("role", "status");
      t.setAttribute("aria-live", "polite");
      lightbox.appendChild(t);
    }
    t.textContent = msg;
    t.classList.toggle("ugc-toast--error", kind === "error");
    requestAnimationFrame(() => t.classList.add("is-shown"));
    clearTimeout(showUGCToast._tid);
    showUGCToast._tid = setTimeout(() => {
      t.classList.remove("is-shown");
      setTimeout(() => t.remove(), 200);
    }, 1800);
  }

  // --- Description utilities -------------------------------------------------
  // Strip <style>/<script> and inline styles so product HTML can’t restyle theme
  function sanitizeDescription(html){
    if (!html) return "";
    const tpl = document.createElement("template");
    tpl.innerHTML = html;

    tpl.content.querySelectorAll("script,style,link,meta,iframe,noscript").forEach(n => n.remove());
    tpl.content.querySelectorAll("*").forEach(el => {
      el.removeAttribute("style");
      [...el.attributes].forEach(a => { if (/^on/i.test(a.name)) el.removeAttribute(a.name); });
    });
    return tpl.innerHTML;
  }
  // Make only first details open; clicking a summary closes others (accordion)
  function initDescriptionBoxes(scope){
    const wrap = scope.querySelector(".ugc-product__description");
    if (!wrap) return;
    const boxes = Array.from(wrap.querySelectorAll(".custom-tabs details"));
    if (!boxes.length) return;

    boxes.forEach((d,i) => { d.open = (i === 0); });
    wrap.addEventListener("click", (ev) => {
      const sum = ev.target.closest("summary");
      if (!sum) return;
      const d = sum.closest("details");
      if (!d) return;
      setTimeout(() => {
        if (d.open) boxes.forEach(x => { if (x !== d) x.open = false; });
      }, 0);
    });
  }

  // --- 9:16 reel that fits the visible viewport on mobile --------------------
  function forceReelNineSixteen(){
    if (!window.matchMedia("(max-width: 749px)").matches) return;
    if (!lightbox.classList.contains("active")) return;
    if (lightbox.classList.contains("ugc-mobile-details")) return; // product view

    const inner = lightbox.querySelector(".ugc-lightbox__inner");
    const wrap  = lightbox.querySelector(".ugc-lightbox__video");

    const viewportH =
      (window.visualViewport && Math.round(window.visualViewport.height)) ||
      (window.innerHeight || document.documentElement.clientHeight || 0);

    const reserve = 72; // breathing room for sticky card / safe-areas
    const maxH = Math.max(320, viewportH - reserve);

    if (inner) {
      inner.style.setProperty("width", "100%", "important");
      inner.style.setProperty("padding", "0", "important");
      inner.style.setProperty("gap", "0", "important");
      inner.style.setProperty("display", "flex", "important");
      inner.style.setProperty("flex-direction", "column", "important");
      inner.style.setProperty("overflow", "hidden", "important");
      inner.style.setProperty("height", "auto", "important");
    }
    if (wrap) {
      wrap.style.setProperty("width", "100%", "important");
      wrap.style.setProperty("height", "auto", "important");
      wrap.style.setProperty("max-height", maxH + "px", "important");
      wrap.style.setProperty("aspect-ratio", "9 / 16", "important");
      wrap.style.setProperty("margin", "0 auto", "important");
      wrap.style.setProperty("position", "relative", "important");
      wrap.style.setProperty("overflow", "hidden", "important");
      wrap.style.setProperty("background", "#000", "important");
    }

    videoEl.style.setProperty("width", "100%", "important");
    videoEl.style.setProperty("height", "100%", "important");
    videoEl.style.setProperty("object-fit", "cover", "important");
    videoEl.style.setProperty("object-position", "center center", "important");
    videoEl.style.setProperty("border-radius", "0", "important");
    videoEl.style.setProperty("background", "#000", "important");
    videoEl.style.setProperty("max-height", "none", "important");
  }
  function clearReelInline(){
    const inner = lightbox.querySelector(".ugc-lightbox__inner");
    const wrap = lightbox.querySelector(".ugc-lightbox__video");
    if (inner) Object.assign(inner.style, { height: "", overflow: "", padding: "", gap: "", display:"", flexDirection:"", width:"", });
    if (wrap)  Object.assign(wrap.style,  { height: "", overflow: "", display: "", maxHeight:"", aspectRatio:"", margin:"", position:"", background:"" });
    Object.assign(videoEl.style, { maxHeight: "", height: "", width: "", objectFit: "", objectPosition:"", borderRadius:"", background:"" });
  }

  // DESKTOP-only balance + MOBILE UI bits (audio btn + reel controls styles)
  (function ensureUGCStyles(){
    const old = document.getElementById("ugc-lb-fixes");
    if (old) old.remove();

    const css = `
    /* === UGC Lightbox injected CSS — CTA pill, spacing, desktop CTA nudge, desc boxes === */

    /* Global vars */
    :root{
      --ugc-card-cta-nudge:-12px;
      --ugc-card-title-gap:2px;
    }

    /* Brand + shape + spacing for the lightbox product meta */
    .ugc-lightbox{
      --ugc-accent:#ff4d80;
      --ugc-accent-press:#e23a6c;
      --ugc-radius:9999px;

      /* Lightbox spacing controls */
      --ugc-meta-gap: 12px;
      --ugc-rev-mt: 0px;
      --ugc-rev-mb: 0px;
      --ugc-price-mt: 0px;
      --ugc-price-mb: 0px;
      --ugc-atc-mt: 0px;
    }

    /* ===== Desktop carousel card ===== */
    @media (min-width:750px){
      .ugc-home__item .ugc-sticky-bar{
        display:flex; align-items:center; gap:10px; padding-right:12px;
      }
      .ugc-home__item .ugc-sticky-bar__title{ margin:0 0 var(--ugc-card-title-gap) !important; }
      .ugc-home__item .ugc-sticky-bar .ugc-sticky-bar__price,
      .ugc-home__item .ugc-sticky-bar .ugc-sticky-bar__reviews{ margin:0 !important; }
      .ugc-home__item .ugc-sticky-bar .button,
      .ugc-home__item .ugc-sticky-bar button,
      .ugc-home__item .ugc-sticky-bar a.button{
        margin-left:auto; align-self:center; transform: translateY(var(--ugc-card-cta-nudge));
      }
    }

    /* ratings (lightbox + card shared classnames) */
    .ugc-product__reviews,
    .ugc-sticky-bar__reviews{
      display:flex; align-items:center; gap:6px; font-weight:600; line-height:1;
      margin: var(--ugc-rev-mt) 0 var(--ugc-rev-mb) !important;
    }
    .ugc-star{ font-size:14px; line-height:1; }
    .ugc-rating-number{ font-size:14px; }
    .ugc-rating-count{ opacity:.85; font-size:13px; }

    /* Force YELLOW ratings everywhere (desktop & mobile) */
    .ugc-sticky-bar__reviews .ugc-star,
    .ugc-sticky-bar__reviews .ugc-rating-number,
    .ugc-sticky-bar__reviews .ugc-rating-count,
    .ugc-lightbox .ugc-product__reviews .ugc-star,
    .ugc-lightbox .ugc-product__reviews .ugc-rating-number,
    .ugc-lightbox .ugc-product__reviews .ugc-rating-count,
    .ugc-mobile-sticky .ugc-ms-reviews .ugc-star,
    .ugc-mobile-sticky .ugc-ms-reviews .ugc-rating-number,
    .ugc-mobile-sticky .ugc-ms-reviews .ugc-rating-count{ color:#FFC107 !important; }

    /* ===== Lightbox spacing & CTA pill (no glow/outline ever) ===== */
    .ugc-lightbox .ugc-product__meta{ display:flex; flex-direction:column; gap:var(--ugc-meta-gap); }
    @media (min-width:750px){ .ugc-lightbox .ugc-product__title{ margin:12px 0 0 !important; } }
    @media (max-width:749px){ .ugc-lightbox .ugc-product__title{ margin:0 !important; } }

    .ugc-lightbox .ugc-product__price{ margin: var(--ugc-price-mt) 0 var(--ugc-price-mb) !important; }

    .ugc-lightbox .ugc-product__meta .button,
    .ugc-lightbox .ugc-product__meta button,
    .ugc-lightbox .ugc-product__meta a.button,
    .ugc-lightbox .ugc-product__meta .button.button--primary,
    .ugc-lightbox .ugc-product__meta button.button--primary,
    .ugc-lightbox .ugc-product__meta a.button.button--primary{
      display:inline-flex; align-items:center; justify-content:center;
      padding:12px 20px; min-height:44px; min-width:180px;
      border-radius:var(--ugc-radius);
      background:var(--ugc-accent) !important;
      color:#fff !important; border:0 !important;
      font-weight:700; font-size:16px; line-height:1;
      text-decoration:none !important; cursor:pointer;
      -webkit-tap-highlight-color:transparent;
      box-shadow:none !important; filter:none !important; text-shadow:none !important;
      transition:none !important; transform:none !important; outline:0 !important;
      appearance:none; -webkit-appearance:none;
      margin-top: var(--ugc-atc-mt) !important;
    }
    .ugc-lightbox .ugc-product__meta .button:hover,
    .ugc-lightbox .ugc-product__meta .button:active,
    .ugc-lightbox .ugc-product__meta .button:focus,
    .ugc-lightbox .ugc-product__meta .button:focus-visible,
    .ugc-lightbox .ugc-product__meta button:hover,
    .ugc-lightbox .ugc-product__meta button:active,
    .ugc-lightbox .ugc-product__meta button:focus,
    .ugc-lightbox .ugc-product__meta button:focus-visible,
    .ugc-lightbox .ugc-product__meta a.button:hover,
    .ugc-lightbox .ugc-product__meta a.button:active,
    .ugc-lightbox .ugc-product__meta a.button:focus,
    .ugc-lightbox .ugc-product__meta a.button:focus-visible{
      background:var(--ugc-accent) !important;
      outline:0 !important; box-shadow:none !important; filter:none !important; transform:none !important; animation:none !important;
    }
    .ugc-lightbox .ugc-product__meta .button::before,
    .ugc-lightbox .ugc-product__meta .button::after,
    .ugc-lightbox .ugc-product__meta button::before,
    .ugc-lightbox .ugc-product__meta button::after,
    .ugc-lightbox .ugc-product__meta a.button::before,
    .ugc-lightbox .ugc-product__meta a.button::after{ content:none !important; display:none !important; }

    @media (max-width:749px){
      .ugc-lightbox .ugc-product__meta .button,
      .ugc-lightbox .ugc-product__meta button,
      .ugc-lightbox .ugc-product__meta a.button{
        width:100%; padding:14px 22px; font-size:17px;
      }
    }

    /* ===== Description: white boxes, dark text (scoped) ===== */
    .ugc-lightbox .ugc-product__description{ font-size:14px; line-height:1.55; color:#111; }
    .ugc-lightbox .ugc-product__description p{ margin:0 0 8px; }
    .ugc-lightbox .ugc-product__description a{ color:var(--ugc-accent); text-decoration:underline; }

    .ugc-lightbox .ugc-product__description .custom-tabs details{
      background:#fff; color:#111;
      border:1px solid #e5e7eb; border-radius:10px;
      padding:16px; margin:16px 0;
      transition:box-shadow .2s ease, border-color .2s ease;
    }
    .ugc-lightbox .ugc-product__description .custom-tabs details[open]{
      box-shadow:0 1px 2px rgba(0,0,0,.06); border-color:#d1d5db;
    }
    .ugc-lightbox .ugc-product__description .custom-tabs summary{
      font-size:18px; font-weight:700; margin:0 0 10px; cursor:pointer; list-style:none; color:#111;
    }
    .ugc-lightbox .ugc-product__description .custom-tabs summary::-webkit-details-marker{ display:none; }
    .ugc-lightbox .ugc-product__description ul{ padding-left:18px; margin:8px 0; }
    .ugc-lightbox .ugc-product__description li{ margin:4px 0; }

    /* Layout / media rules */
    @media (min-width:750px){
      .ugc-lightbox .ugc-lightbox__inner{max-width:1080px!important;gap:16px!important;padding:16px!important;display:flex!important}
      .ugc-lightbox .ugc-lightbox__video{flex:0 1 52%!important}
      .ugc-lightbox .ugc-lightbox__product{flex:0 1 48%!important;max-height:80vh!important;overflow:auto!important}
      .ugc-lightbox .ugc-product__main{height:56vh!important;max-height:56vh!important;overflow:hidden;border-radius:10px}
      .ugc-lightbox .ugc-pmain{width:100%!important;height:100%!important;object-fit:contain!important;display:block;border-radius:10px}
      .ugc-lightbox .ugc-lightbox__video video{width:100%;height:auto;max-height:80vh!important;border-radius:12px;background:#000}
    }
    @media (max-width:749px){
      .ugc-audio-toggle{
        position:fixed; top:calc(env(safe-area-inset-top) + 10px); right:56px; z-index:100001;
        display:flex; align-items:center; justify-content:center;
        width:40px; height:40px; border-radius:999px; border:none;
        background:rgba(0,0,0,.55); color:#fff; backdrop-filter: blur(10px) saturate(140%);
      }
      .ugc-audio-toggle .ugc-audio-icn{ width:18px; height:18px; display:block; }
      .ugc-audio-toggle svg{ width:100%; height:100%; display:block; }
      .ugc-lightbox.ugc-mobile-details .ugc-audio-toggle{ display:none !important; }
      .ugc-sr{ position:absolute !important; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,1px,1px); white-space:nowrap; border:0; }
    }`;
    const st = document.createElement("style");
    st.id = "ugc-lb-fixes"; st.textContent = css;
    document.head.appendChild(st);
  })();

  // Maintain a reliable 100vh for iOS toolbars (used by your CSS variable too)
  function setVHVar(){
    const vh = (window.innerHeight || document.documentElement.clientHeight || 0) * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
  }
  setVHVar();
  window.addEventListener("resize", () => { setVHVar(); forceReelNineSixteen(); });
  window.addEventListener("orientationchange", () => { setVHVar(); forceReelNineSixteen(); });

  // --- Mobile AUDIO TOGGLE ---------------------------------------------------
  function ensureAudioToggle(){
    if (!window.matchMedia("(max-width: 749px)").matches) return null;
    let btn = lightbox.querySelector(".ugc-audio-toggle");
    if (!btn) {
      btn = document.createElement("button");
      btn.className = "ugc-audio-toggle";
      btn.type = "button";
      btn.innerHTML = `
        <span class="ugc-audio-icn" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round">
            <path class="spk" d="M11 5l-4 4H4v6h3l4 4z"></path>
            <path class="waves on" d="M15.5 8.5a5 5 0 010 7M18 6a8 8 0 010 12"></path>
            <path class="mute" style="display:none" d="M22 2L2 22"></path>
          </svg>
        </span>
        <span class="ugc-sr">Toggle sound</span>`;
      btn.addEventListener("click", () => {
        videoEl.muted = !videoEl.muted;
        if (!videoEl.muted) { videoEl.play().catch(()=>{}); }
        syncAudioToggle();
      });
      lightbox.appendChild(btn);
      videoEl.addEventListener("volumechange", syncAudioToggle);
    }
    syncAudioToggle();
    return btn;
  }
  function syncAudioToggle(){
    const btn = lightbox.querySelector(".ugc-audio-toggle");
    if (!btn) return;
    const on = !videoEl.muted;
    btn.setAttribute("aria-pressed", String(on));
    btn.title = on ? "Mute" : "Unmute";
    const waves = btn.querySelector(".waves.on");
    const slash = btn.querySelector(".mute");
    if (waves) waves.style.display = on ? "block" : "none";
    if (slash) slash.style.display = on ? "none" : "block";
  }

  // ===== Remove mobile play/pause button + slider; keep tap-to-toggle ========
  let __rcHideTid = null, __isScrubbing = false, __rcRaf = null;

  // (keep adaptive duration helpers untouched in case you reuse later)
  let __approxTotal = 0;
  function bufferedEndSafe(v){ try{ if (v?.buffered?.length) return v.buffered.end(v.buffered.length - 1); }catch{} return 0; }
  function seekableEndSafe(v){ try{ if (v?.seekable?.length) return v.seekable.end(v?.seekable.length - 1); }catch{} return 0; }
  function updateAdaptiveTotal(){
    const d = Number.isFinite(videoEl.duration) ? videoEl.duration : 0;
    const b = bufferedEndSafe(videoEl);
    const s = seekableEndSafe(videoEl);
    __approxTotal = Math.max(__approxTotal, d, b, s, videoEl.currentTime + 0.25);
  }
  function getAdaptiveTotal(){ return __approxTotal > 0 ? __approxTotal : 1; }

  // NEW: keep simple tap-to-toggle on mobile reel view
  function ensureTapToToggle(){
    if (!window.matchMedia("(max-width: 749px)").matches) return;
    const wrap = lightbox.querySelector(".ugc-lightbox__video");
    if (!wrap || wrap._tapBound) return;
    wrap.addEventListener("click", ()=>{
      if (lightbox.classList.contains("ugc-mobile-details")) return;
      if (videoEl.paused) videoEl.play().catch(()=>{}); else videoEl.pause();
    }, { passive:true });
    wrap._tapBound = true;
  }

  // Remove custom controls (we're using native)
  function ensureReelControls(){
    if (!window.matchMedia("(max-width: 749px)").matches) return null;
    const rc = lightbox.querySelector(".ugc-reel-controls");
    if (rc && typeof rc._stopRCraf === "function") rc._stopRCraf();
    rc?.remove();
    ensureTapToToggle();
    return null;
  }

  function showReelControls(){ /* no-op since custom controls are removed */ }

  // Toggle helpers (mobile)
  function enterProductView() {
    const overlay = lightbox.querySelector(".ugc-mobile-sticky");
    overlay && (overlay.style.display = "none");
    lightbox.classList.add("ugc-mobile-details");
    productEl.style.display = "";
    // show controls in product view
    videoEl.setAttribute("controls", "");
    videoEl.controls = true;
    // hide reel wrapper so gallery owns the screen
    const wrap = lightbox.querySelector(".ugc-lightbox__video");
    if (wrap) wrap.style.display = "none";
    videoEl.pause();

    // Back button
    let back = lightbox.querySelector(".ugc-back-to-reel");
    if (!back) {
      back = document.createElement("button");
      back.className = "ugc-back-to-reel";
      back.type = "button";
      back.textContent = "Back to Reel";
      back.addEventListener("click", enterReelView);
      lightbox.appendChild(back);
    }
    lightbox.querySelector(".ugc-reel-controls")?.remove();

    clearReelInline();
    productEl.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function enterReelView() {
    lightbox.classList.remove("ugc-mobile-details");
    productEl.style.display = "none";
    const overlay = lightbox.querySelector(".ugc-mobile-sticky");
    overlay && (overlay.style.display = "");
    const back = lightbox.querySelector(".ugc-back-to-reel");
    back && back.remove();

    // keep native controls on mobile
    videoEl.setAttribute("controls", "");
    videoEl.controls = true;

    const wrap = lightbox.querySelector(".ugc-lightbox__video");
    if (wrap) wrap.style.display = "";
    forceReelNineSixteen();
    ensureAudioToggle();
    ensureReelControls();
    ensureTapToToggle();
    requestAnimationFrame(() => videoEl.play().catch(()=>{}));
    lightbox.scrollTop = 0;
  }

  // --- Open Lightbox ---------------------------------------------------------
  document.querySelectorAll("[data-open-reel]").forEach(btn => {
    btn.addEventListener("click", e => {
      const reel = e.target.closest(".ugc-home__item");
      if (!reel) return;

      const { src: reelSrc, poster: reelPoster } = getReelMedia(reel);
      const title  = reel.querySelector(".ugc-sticky-bar__title")?.innerText || "";
      const price  = reel.querySelector(".ugc-sticky-bar__price")?.innerHTML || "";
      const handle = reel.querySelector("[data-product-handle]")?.dataset.productHandle;
      const thumb  = reel.querySelector(".ugc-sticky-bar img")?.src || null;
      const isMobile = window.matchMedia("(max-width: 749px)").matches;

      // rating from opener (Liquid should set data attrs on opener)
      const openerEl = reel.querySelector("[data-product-handle]");
      const rating = parseFloat(openerEl?.dataset.rating || "0");
      const ratingCount = parseInt(openerEl?.dataset.ratingCount || "0", 10);

      setVHVar();

      // Controls ON
      videoEl.setAttribute("controls", "");
      videoEl.controls = true;
      if (isMobile) {
        videoEl.muted = true;  // autoplay-safe; unmute via button
        videoEl.setAttribute("muted", "");
      }

      // Reset adaptive total for new video
      __approxTotal = 0;

      // Reset & load video
      try {
        cleanupVideo();
        if (reelPoster) videoEl.poster = reelPoster;
        if (reelSrc) {
          const s = document.createElement("source");
          s.src = reelSrc; s.type = "video/mp4";
          videoEl.appendChild(s);
          videoEl.addEventListener("error", cleanupVideo, { once:true });
          videoEl.load();
          videoEl.currentTime = 0;
          requestAnimationFrame(() => videoEl.play().catch(() => {}));
        }
      } catch {}

      // --- Load product (gallery + thumbs grid + description)
      if (handle) {
        fetch(`/products/${handle}.js`)
          .then(res => res.json())
          .then(productData => {
            const titleText   = productData.title || title;
            const variantId   = productData.variants?.[0]?.id;
            const imagesArray = (productData.images || []).map(i => (typeof i === "string" ? i : i?.src)).filter(Boolean);

            const withWidth = (url, w) => {
              try { const sep = url.includes("?") ? "&" : "?"; return url.includes("cdn.shopify.com") ? `${url}${sep}width=${w}` : url; }
              catch { return url; }
            };

            const mainSrc = imagesArray[0] || "";

            // rating row HTML for lightbox
            const ratingDisplay = (ratingCount > 0)
              ? `<div class="ugc-product__reviews" aria-label="Rating ${(rating || 0).toFixed(1)} out of 5 from ${ratingCount} reviews">
                   <span class="ugc-star">★</span>
                   <span class="ugc-rating-number">${(rating || 0).toFixed(1)}</span>
                   <span class="ugc-rating-count">(${ratingCount})</span>
                 </div>`
              : "";

            // sanitize product description
            const rawDesc = productData.body_html || productData.description || "";
            const descHTML = sanitizeDescription(rawDesc);

            const thumbsBtns = imagesArray.map((src, i) => `
              <button class="ugc-pthumb ${i===0?'is-active':''}" data-idx="${i}" aria-label="View image ${i+1}" style="aspect-ratio:1/1;">
                <img src="${withWidth(src, 240)}" alt="${titleText} thumbnail ${i+1}" style="width:100%;height:100%;object-fit:cover;display:block;">
              </button>
            `).join("");

            productEl.innerHTML = `
              <div class="ugc-product">
                <div class="ugc-product__gallery">
                  <div class="ugc-product__main">
                    ${mainSrc ? `<img class="ugc-pmain" src="${withWidth(mainSrc, 1200)}" alt="${titleText}">` : ""}
                  </div>
                  ${imagesArray.length ? `
                    <div class="ugc-product__thumbs-grid" data-n="${imagesArray.length}" style="margin-top:10px;display:grid;gap:8px;">
                      ${thumbsBtns}
                    </div>` : ""}
                </div>
                <div class="ugc-product__meta">
                  <h3 class="ugc-product__title">${titleText}</h3>
                  ${ratingDisplay}
                  <div class="ugc-product__price">${price}</div>
                  ${variantId ? `<button class="button button--primary" data-variant-id="${variantId}">Add to Cart</button>` : ""}
                  ${descHTML ? `<div class="ugc-product__description">${descHTML}</div>` : ""}
                </div>
              </div>
            `;

            // Make description boxes behave (first open, accordion)
            initDescriptionBoxes(productEl);

            productEl.querySelector(".ugc-product__thumbs-grid")?.addEventListener("click", (ev) => {
              const btn = ev.target.closest(".ugc-pthumb");
              if (!btn) return;
              const idx  = Number(btn.dataset.idx || 0);
              const next = imagesArray[idx];
              productEl.querySelectorAll(".ugc-pthumb").forEach(b => b.classList.remove("is-active"));
              btn.classList.add("is-active");
              const mainImg = productEl.querySelector(".ugc-pmain");
              if (mainImg && next) mainImg.src = withWidth(next, 1200);
            }, { passive: true });

            layoutThumbGrid(productEl.querySelector(".ugc-product__thumbs-grid"));

            // ----- MOBILE: reel-first + sticky card overlay + two-way toggle
            if (isMobile) {
              productEl.style.display = "none";
              const wrap = lightbox.querySelector(".ugc-lightbox__video");
              wrap.style.display = "";
              forceReelNineSixteen();

              // overlay card
              let overlay = lightbox.querySelector(".ugc-mobile-sticky");
              if (!overlay) {
                overlay = document.createElement("div");
                overlay.className = "ugc-mobile-sticky";
                overlay.innerHTML = `
                  <button class="ugc-ms-card">
                    <img class="ugc-ms-img" alt="">
                    <div class="ugc-ms-meta">
                      <span class="ugc-ms-title"></span>
                      <div class="ugc-ms-price-and-rev">
                        <span class="ugc-ms-price"></span>
                        <span class="ugc-ms-reviews" aria-live="polite">
                          <span class="ugc-star">★</span>
                          <span class="ugc-rating-number"></span>
                          <span class="ugc-rating-count"></span>
                        </span>
                      </div>
                    </div>
                    <span class="ugc-ms-cta">View</span>
                  </button>`;
                lightbox.appendChild(overlay);
              }
              overlay.style.display = "";
              overlay.querySelector(".ugc-ms-img").src = thumb || mainSrc || "";
              overlay.querySelector(".ugc-ms-title").textContent = title || (productData.title || "");
              overlay.querySelector(".ugc-ms-price").innerHTML = price || "";

              // populate/hide reviews on overlay
              const revWrap = overlay.querySelector(".ugc-ms-reviews");
              const revNum  = overlay.querySelector(".ugc-rating-number");
              const revCnt  = overlay.querySelector(".ugc-rating-count");
              if (ratingCount > 0) {
                revWrap.style.display = "inline-flex";
                revNum.textContent = (rating || 0).toFixed(1);
                revCnt.textContent = `(${ratingCount})`;
                revWrap.setAttribute("aria-label", `Rating ${(rating || 0).toFixed(1)} out of 5 from ${ratingCount} reviews`);
              } else {
                revWrap.style.display = "none";
              }

              overlay.onclick = () => enterProductView();

              ensureAudioToggle();
              ensureReelControls();
              ensureTapToToggle();

              lightbox.classList.remove("ugc-mobile-details");
            } else {
              // desktop
              productEl.style.display = "";
              lightbox.querySelector(".ugc-mobile-sticky")?.remove();
              lightbox.querySelector(".ugc-audio-toggle")?.remove();
              lightbox.querySelector(".ugc-reel-controls")?.remove();
              lightbox.classList.remove("ugc-mobile-details");
            }
          })
          .catch(() => {
            productEl.innerHTML = `<p style="padding:8px;">Could not load product info.</p>`;
          });
      }

      // lock background scroll on mobile while lightbox open
      document.documentElement.classList.add("ugc-no-scroll");
      document.body.classList.add("ugc-no-scroll");

      lightbox.classList.add("active");
      lightbox.scrollTop = 0;

      // mount UI immediately on mobile
      if (isMobile) {
        ensureReelControls();
        ensureAudioToggle();
        ensureTapToToggle();
      }

      setTimeout(forceReelNineSixteen, 50);
    });
  });

  // --- Close Lightbox --------------------------------------------------------
  lightbox.querySelector(".ugc-lightbox__close").addEventListener("click", () => {
    lightbox.classList.remove("active");
    cleanupVideo();

    // cleanup mobile state
    const rc = lightbox.querySelector(".ugc-reel-controls");
    rc?._stopRCraf && rc._stopRCraf();
    lightbox.querySelector(".ugc-mobile-sticky")?.remove();
    lightbox.querySelector(".ugc-back-to-reel")?.remove();
    lightbox.querySelector(".ugc-audio-toggle")?.remove();
    lightbox.querySelector(".ugc-reel-controls")?.remove();
    lightbox.querySelectorAll(".ugc-toast")?.forEach(t => t.remove()); // remove any toasts
    lightbox.classList.remove("ugc-mobile-details");
    productEl.style.display = "";

    clearReelInline();

    document.documentElement.classList.remove("ugc-no-scroll");
    document.body.classList.remove("ugc-no-scroll");
  });

  // --- AJAX Add to Cart + toast ---------------------------------------------
  productEl.addEventListener("click", e => {
    const btn = e.target.closest("[data-variant-id]");
    if (!btn) return;

    // prevent rapid double-taps while posting
    if (btn._busy) return;
    btn._busy = true;

    const id = btn.dataset.variantId;
    const titleText = productEl.querySelector(".ugc-product__title")?.textContent?.trim() || "Item";

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, quantity: 1 })
    })
    .then(res => {
      if (!res.ok) throw new Error("Add failed");
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      showUGCToast(`Added “${titleText}” to cart`);
    })
    .catch(() => {
      showUGCToast("Couldn’t add to cart. Try again.", "error");
    })
    .finally(() => { btn._busy = false; });
  });

  // --- Autoplay Only Visible Reels + 3–4s preview loop -----------------------
  const PREVIEW_SECONDS = 4;
  const thumbVideos = document.querySelectorAll(".ugc-thumb-video");
  thumbVideos.forEach(v => {
    v.muted = true; v.playsInline = true; v.loop = false;
    const setupLoop = () => { v._previewEnd = Math.min(PREVIEW_SECONDS, (isFinite(v.duration) ? v.duration : PREVIEW_SECONDS)); };
    if (v.readyState >= 1) setupLoop(); else v.addEventListener("loadedmetadata", setupLoop, { once: true });
    v.addEventListener("timeupdate", () => {
      const end = v._previewEnd || PREVIEW_SECONDS;
      if (v.currentTime >= end) { try { v.currentTime = 0.01; } catch(e) { v.currentTime = 0; } v.play().catch(() => {}); }
    });
  });
  if (thumbVideos.length) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        if (isIntersecting) target.play().catch(() => {});
        else { target.pause(); try { target.currentTime = 0.01; } catch(e) { target.currentTime = 0; } }
      });
    }, { threshold: 0.3 });
    thumbVideos.forEach(video => observer.observe(video));
  }

  // --- Carousel Arrows with wrap-around (UGC home strip) ---------------------
  const carousel = document.querySelector(".ugc-home__carousel");
  function ugcScrollStep() {
    if (!carousel) return 300;
    const firstItem = carousel.querySelector(".ugc-home__item");
    const gap = parseFloat(getComputedStyle(carousel).gap || "0");
    const theW = firstItem ? firstItem.getBoundingClientRect().width : 300;
    return theW + gap;
  }
  const items = carousel ? carousel.querySelectorAll(".ugc-home__item") : [];
  let currentIndex = 0;
  function scrollToIndex(idx) {
    if (!carousel || !items.length) return;
    currentIndex = (idx + items.length) % items.length;
    const item = items[currentIndex];
    if (item) {
      const offset = item.offsetLeft - (carousel.clientWidth - item.clientWidth) / 2;
      carousel.scrollTo({ left: offset, behavior: "smooth" });
    }
  }
  document.querySelector(".ugc-carousel-prev")?.addEventListener("click", () => { scrollToIndex(currentIndex - 1); });
  document.querySelector(".ugc-carousel-next")?.addEventListener("click", () => { scrollToIndex(currentIndex + 1); });

  // --- Mobile center focus + ensure autoplay for 3 visible tiles -------------
  (function(){
    if (!window.matchMedia("(max-width: 749px)").matches) return;
    const track = document.querySelector(".ugc-home__carousel");
    const items = Array.from(document.querySelectorAll(".ugc-home__item"));
    const videos = items.map(i => i.querySelector(".ugc-thumb-video")).filter(Boolean);
    if (!track || !items.length) return;

    function getStep(){
      const first = items[0];
      const gap = parseFloat(getComputedStyle(track).gap || "0");
      const w = first ? first.getBoundingClientRect().width : 300;
      return w + gap;
    }
    function clamp(n, min, max){ return Math.max(min, Math.min(max, n)); }
    function getCenterIndex(){
      const step = getStep(); if (!step) return 0;
      const leftIdx = Math.round(track.scrollLeft / step);
      return clamp(leftIdx + 1, 0, items.length - 1);
    }
    function applyCenter(){
      const c = getCenterIndex();
      items.forEach((el, i) => el.classList.toggle("is-center", i === c));
      videos.forEach((v, i) => { (i >= c - 1 && i <= c + 1) ? v?.play?.().catch(()=>{}) : v?.pause?.(); });
    }
    let ticking = false;
    track.addEventListener("scroll", () => {
      if (ticking) return; ticking = true;
      requestAnimationFrame(() => { applyCenter(); ticking = false; });
    });
    window.addEventListener("resize", () => { requestAnimationFrame(applyCenter); });
    const prev = document.querySelector(".ugc-carousel-prev");
    const next = document.querySelector(".ugc-carousel-next");
    const afterNavUpdate = () => setTimeout(applyCenter, 280);
    prev?.addEventListener("click", afterNavUpdate);
    next?.addEventListener("click", afterNavUpdate);
    applyCenter();
  })();

  // ===== Thumb grid layout (1 or 2 neat rows, squares auto-sized, no scroll) =
  function layoutThumbGrid(grid){
    if (!grid) return;
    const MIN = 64;
    const GAP = 8;
    const n = Number(grid.dataset.n || grid.children.length || 0);
    const rows = n <= 5 ? 1 : 2;
    grid.dataset.rows = rows;

    function compute(){
      const W = grid.clientWidth || 0; if (!W) return;
      let bestCols = Math.min(n, rows === 1 ? n : Math.ceil(n / rows));
      for (let c = bestCols; c >= 2; c--){
        const totalGaps = GAP * (c - 1);
        const cell = Math.floor((W - totalGaps) / c);
        if (cell >= MIN){ bestCols = c; break; }
      }
      const targetCols = rows === 1 ? Math.min(n, bestCols)
                                    : Math.min(Math.max(Math.ceil(n / 2), 2), bestCols);
      grid.style.gridTemplateColumns = `repeat(${targetCols}, 1fr)`;
      grid.style.gridAutoRows = "1fr";
      grid.querySelectorAll(".ugc-pthumb").forEach(b => { b.style.width = "100%"; b.style.aspectRatio = "1 / 1"; });
    }

    compute();
    const ro = new ResizeObserver(() => compute());
    ro.observe(grid);
  }
});

// keep page behind from scrolling
try{
  const s=document.createElement("style");
  s.textContent=".ugc-no-scroll{overflow:hidden!important}";
  document.head.appendChild(s);
}catch{}
